import { RevealGroup, RevealItem } from '@/components/Reveal';
import Typography from '@/components/ui/typography';

interface BannerProps {
  image: string;
  alt: string;
  tagline?: string;
  title: string;
  description: string;
  /**
   * Semantic level for the banner title. Every landing page uses Banner, so it
   * carries the page's <h1> by default — previously it rendered an <h3> and no
   * public page had an h1 at all. Pass 'h2' where a page already has one.
   */
  headingLevel?: 'h1' | 'h2';
  /**
   * Layout hints only, for pages that know their image's real dimensions in
   * advance — the image is still stretched to fill its column via CSS.
   */
  width?: number;
  height?: number;
  children?: React.ReactNode;
}

/**
 * The page header every landing and detail page opens with.
 *
 * Ink panel, image alongside. The image is the LCP element on most public
 * pages, so it is deliberately not lazy-loaded and is set `eager` with a high
 * fetch priority.
 */
const Banner = ({
  image,
  alt,
  tagline,
  title,
  description,
  headingLevel = 'h1',
  width,
  height,
  children,
}: BannerProps) => {
  const Heading =
    headingLevel === 'h1' ? Typography.Display1 : Typography.Display2;

  return (
    <section className="w-full bg-ink text-ink-foreground">
      <div className="grid md:grid-cols-2">
        <RevealGroup
          trigger="load"
          className="order-2 flex min-w-0 flex-col items-start justify-center gap-5 px-5 py-12 md:order-1 md:px-8 md:py-20 lg:px-12 lg:py-24"
        >
          {tagline && (
            <RevealItem className="flex flex-col gap-3">
              <Typography.Eyebrow>{tagline}</Typography.Eyebrow>
              <div aria-hidden className="seam seam-on-ink w-24" />
            </RevealItem>
          )}
          <RevealItem>
            <Heading className="max-w-[18ch]">{title}</Heading>
          </RevealItem>
          <RevealItem>
            <Typography.Body className="max-w-prose text-ink-foreground/75">
              {description}
            </Typography.Body>
          </RevealItem>
          {/*
            w-full min-w-0: the column is `items-start`, so without a width a
            child sizes to fit-content — and a carousel strip's max-content is
            every slide laid end to end. The shoppe header passes one in, and it
            dragged the column wider than its track.
          */}
          {children && (
            <RevealItem className="w-full min-w-0">{children}</RevealItem>
          )}
        </RevealGroup>

        {/*
          min-w-0 on both columns.

          A grid item defaults to min-width:auto, which resolves to its
          content's min-content width — and for a column whose content is an
          <img>, that is the image's intrinsic width. A wide banner photograph
          therefore pushed its track past the 1fr it was given and took the
          whole page into horizontal scroll. w-full sets the width; only
          min-w-0 lets the track actually shrink to it.
        */}
        <div className="relative order-1 min-h-56 min-w-0 md:order-2 md:min-h-112">
          {/* Scrim only where the panel meets the image, so the photograph
              stays readable instead of being flooded to near-black. */}
          <div
            aria-hidden
            className="absolute inset-0 bg-linear-to-t from-ink via-ink/25 to-transparent md:bg-linear-to-r"
          />
          <img
            className="size-full object-cover"
            src={image}
            alt={alt}
            width={width}
            height={height}
            loading="eager"
            fetchPriority="high"
          />
        </div>
      </div>
    </section>
  );
};

export default Banner;
